import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  registerCustomerUser,
  signInCustomerUser,
  signInWithGoogleCustomerUser,
  signOutCustomerUser,
  watchCustomerSession,
} from '../lib/firebase';

const CustomerAuthContext = createContext();

export function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const unsubscribe = watchCustomerSession(({ user: nextUser, profile: nextProfile, authReady: ready, accessError }) => {
      setUser(nextUser);
      setProfile(nextProfile);
      setAuthReady(Boolean(ready));
      setAuthError(accessError || null);
    });

    return unsubscribe;
  }, []);

  const signUp = async ({ firstName, lastName, email, password }) => {
    setAuthBusy(true);
    setAuthError(null);

    try {
      const createdUser = await registerCustomerUser({ firstName, lastName, email, password });
      return createdUser;
    } catch (error) {
      setAuthError(error);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  };

  const signIn = async ({ email, password }) => {
    setAuthBusy(true);
    setAuthError(null);

    try {
      const signedInUser = await signInCustomerUser(email, password);
      return signedInUser;
    } catch (error) {
      setAuthError(error);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  };

  const signOut = async () => {
    setAuthBusy(true);
    setAuthError(null);

    try {
      await signOutCustomerUser();
    } catch (error) {
      setAuthError(error);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  };

  const signInWithGoogle = async () => {
    setAuthBusy(true);
    setAuthError(null);

    try {
      const signedInUser = await signInWithGoogleCustomerUser();
      return signedInUser;
    } catch (error) {
      setAuthError(error);
      throw error;
    } finally {
      setAuthBusy(false);
    }
  };

  const value = useMemo(() => ({
    user,
    profile,
    authReady,
    authBusy,
    authError,
    isSignedIn: Boolean(user),
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  }), [user, profile, authReady, authBusy, authError]);

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export const useCustomerAuth = () => useContext(CustomerAuthContext);
